/**
 * Lightweight local SEO fixtures for blog + event detail pages.
 *
 * Detail routes are data-driven: with no reachable database (CI, a fresh
 * clone, an offline `npm run build && npm run test:seo:e2e`) every
 * /blog/:slug and /events/:slug renders the not-found state, which by
 * design carries no canonical. That made the canonical assertions
 * unverifiable exactly where canonicals matter most.
 *
 * When fixtures are enabled the services fall back to a synthetic — but
 * structurally complete — row for the requested slug, so the page renders
 * its real Helmet block (canonical, og:url, JSON-LD) without touching the
 * database. This is a FALLBACK only: a real published row always wins, so
 * enabling fixtures never masks live data.
 *
 * Enable with any of:
 *   - REACT_APP_SEO_FIXTURES=1 at build time
 *   - window.__SEO_FIXTURES__ = true   (Playwright init script)
 *   - localStorage.setItem('seoFixtures', '1')
 */

const FIXTURE_DATE = "2026-01-15T09:00:00.000Z";

export function seoFixturesEnabled() {
  if (typeof process !== "undefined" && process.env && process.env.REACT_APP_SEO_FIXTURES === "1") {
    return true;
  }
  if (typeof window === "undefined") return false;
  if (window.__SEO_FIXTURES__ === true) return true;
  try {
    return window.localStorage.getItem("seoFixtures") === "1";
  } catch {
    return false;
  }
}

function titleFromSlug(slug) {
  return String(slug || "sample")
    .split(/[-_/]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

/** A published-looking article row shaped like blogService's normalizeArticle output. */
export function fixtureArticle(slug) {
  const title = titleFromSlug(slug);
  return {
    id: `fixture-article-${slug}`,
    slug,
    title,
    summary: `${title} — a sample Zetech University article used to verify SEO metadata locally.`,
    body: `<h2>${title}</h2><p>Fixture content for local and CI SEO verification.</p>`,
    featured_image: null,
    og_image: null,
    canonical_url: null,
    seo_title: null,
    seo_description: null,
    seo_keywords: null,
    author_name: "Zetech University",
    author: null,
    category: null,
    tags: [],
    reading_time: 3,
    view_count: 0,
    status: "published",
    published_at: FIXTURE_DATE,
    updated_at: FIXTURE_DATE,
    created_at: FIXTURE_DATE,
    __fixture: true,
  };
}

/** A published-looking event row shaped like eventService's normalizeEvent output. */
export function fixtureEvent(slug) {
  const title = titleFromSlug(slug);
  return {
    id: `fixture-event-${slug}`,
    slug,
    title,
    description: `${title} — a sample Zetech University event used to verify SEO metadata locally.`,
    body: `<p>Fixture content for local and CI SEO verification.</p>`,
    featured_image: null,
    og_image: null,
    canonical_url: null,
    seo_title: null,
    seo_description: null,
    seo_keywords: null,
    location: "Zetech University, Main Campus",
    campus: "Main Campus",
    department: null,
    school: null,
    organizer: "Zetech University",
    google_maps: null,
    start_at: FIXTURE_DATE,
    end_at: FIXTURE_DATE,
    registration_deadline: null,
    event_type: "conference",
    event_mode: "in_person",
    meeting_link: null,
    capacity: 100,
    registered_count: 0,
    available_seats: 100,
    register_url: null,
    registration_mode: "external",
    is_free: true,
    price: null,
    timezone: "Africa/Nairobi",
    published: true,
    is_featured: false,
    view_count: 0,
    category: null,
    speakers: [],
    sessions: [],
    gallery: [],
    downloads: [],
    sponsors: [],
    tags: [],
    created_at: FIXTURE_DATE,
    updated_at: FIXTURE_DATE,
    __fixture: true,
  };
}
